import {_} from 'lodash';
import * as ko from "knockout";
import PromiseRequest from "../../../req/PromiseRequest";
import {AbstractDialogOperation, Step} from "./AbstractDialogOperation";
import {ICompanySyncstep, ISyncstep, TSyncfield} from "../../../index";
import {PermissionValidator} from "../../../auth/PermissionValidator";
import {MAX_RESULTS_PER_PAGE, ObjectTypeId, SPINNER, SyncstepId} from "../../../constants";
import {TServiceName} from "../../../constants/ApiServices";
import {global} from "../../../appContext";
import {createErrorMessage, getObjectDisplayName, getObjectType} from "../../../util/misc";
import {nsSyncFields} from "../../../req/nsSyncFields";
import {getSearchObjectParams} from "../../../util/request";
import {nsRuleset} from "../../ruleset/nsRuleset";
import {PLEASE_WAIT_MESSAGE} from "../../../constants/Language";
import {getHash} from "../../../constants/RouteDefinitions";
import convertTuplesToObjectData = nsSyncFields.convertTuplesToObjectData;
import recreateHtml from './recreate.html?raw';

export let template: string = recreateHtml;

export interface IExportStep {
    name: string
    syncstep: ISyncstep
}

export interface IObjectStatusListItem {
    objectData: nsSyncFields.TObjectData_basic
    objectHash: string
    objectName: string
    lines: string[]
}

const OBJECT_ALREADY_IN_REEXPORT_QUEUE = "Object already in reexport queue"

function isObjectAlreadyInReexportQueueError(message: string): boolean {
    return String(message).includes(OBJECT_ALREADY_IN_REEXPORT_QUEUE)
}

function isObjectAlreadyRestoredInfoError(message: string): boolean {
    const s = String(message)
    if (isObjectAlreadyInReexportQueueError(s)) {
        return false
    }
    return (
        s.includes("Object already restored") ||
        s.includes("Object is already restored") ||
        /already[-\s]restored/i.test(s)
    )
}

function getObjectStatusListItemForId(
    objectTypeId: ObjectTypeId,
    objects: nsSyncFields.TObjectData_basic[],
    id: string,
): Pick<IObjectStatusListItem, "objectData" | "objectHash" | "objectName"> {
    const objectData = objects.find((o) => String(o.id) == id)
    let objectHash: string
    switch (objectTypeId) {
        case ObjectTypeId.User:
            objectHash = getHash("userdetails", { id: id }) ?? ''
            break
        case ObjectTypeId.Department:
            objectHash = getHash("departmentdetails", { id: id }) ?? ''
            break
        case ObjectTypeId.Group:
            objectHash = getHash("groupdetails", { id: id }) ?? ''
            break
        case ObjectTypeId.FormData:
            objectHash = getHash("formdatadetails", { id: id }) ?? ''
            break
        default:
            objectHash = ""
            break
    }
    return {
        objectData: objectData!,
        objectHash: objectHash,
        objectName: getObjectDisplayName(objectData ?? {id: Number(id)} as nsSyncFields.TObjectData_basic) || `#${id}`,
    }
}

export class viewModel extends AbstractDialogOperation {
    private readonly pv: PermissionValidator = global.permissionValidator;
    private req_emptycache: PromiseRequest = global.getPromiseRequest('emptycache');
    protected req_getcompanysyncsteps: PromiseRequest = global.getPromiseRequest('getcompanysyncsteps');
    private req_getsyncsteps: PromiseRequest = global.getPromiseRequest('getsyncsteps');
    private req_searchobject: PromiseRequest = global.getPromiseRequest('searchobject');
    protected syncsteps!: ISyncstep[];
    protected serviceName!: TServiceName
    private readonly _selectedExportSteps: KnockoutObservableArray<IExportStep> = ko.observableArray<IExportStep>([])
    /** Empty until Start loads steps; history recreate never fills this, but Init still re-evaluates allSyncstepsChecked. */
    private exportSyncsteps: IExportStep[] = [];
    private successStepResults: { id: number; stepLabel: string }[] = []
    protected restoredList!: IObjectStatusListItem[]
    protected errorsReexportInfo!: IObjectStatusListItem[]
    protected errorsAlreadyRestoredInfo!: IObjectStatusListItem[]
    protected errorsFailure!: IObjectStatusListItem[]

    protected getProcessItemSuccessMessage(): string {
        // do not use
        return ''
    }

    protected successMessage(): string {
        const n = this.submitSuccess.length
        return `((Restored)) [${n}]`
    }

    protected errorMessage(): string {
        const n = this.submitError.filter(
            (e) =>
                !isObjectAlreadyInReexportQueueError(e.message) && !isObjectAlreadyRestoredInfoError(e.message),
        ).length
        return `((Not restored)) [${n}]`
    }

    protected reexportInfoMessage(): string {
        const n = this.submitError.filter((e) => isObjectAlreadyInReexportQueueError(e.message)).length
        return `((Already in export queue)) [${n}]`
    }

    protected alreadyRestoredInfoMessage(): string {
        const n = this.submitError.filter((e) => isObjectAlreadyRestoredInfoError(e.message)).length
        return `((Already restored)) [${n}]`
    }

    private buildObjectStatusListFromLines<T>(
        objects: nsSyncFields.TObjectData_basic[],
        rows: T[],
        getId: (r: T) => number,
        getLine: (r: T) => string,
    ): IObjectStatusListItem[] {
        if (!rows.length) {
            return []
        }
        const byId = _.groupBy(rows, (r: T) => String(getId(r)))
        return _(byId)
            .map((group: T[], id: string) => {
                const { objectData, objectHash, objectName } = getObjectStatusListItemForId(
                    this.objecttypeid,
                    objects,
                    id,
                )
                return {
                    objectData: objectData,
                    objectHash: objectHash,
                    objectName: objectName,
                    lines: group.map((r: T) => getLine(r)),
                }
            })
            .sortBy((o: IObjectStatusListItem) => o.objectName.toLowerCase())
            .value()
    }

    private buildObjectStatusListFromIdToLines(
        objects: nsSyncFields.TObjectData_basic[],
        idToLines: Record<string, string[]>,
    ): IObjectStatusListItem[] {
        const ids = Object.keys(idToLines)
        if (!ids.length) {
            return []
        }
        return _(ids)
            .map((id: string) => {
                const base = getObjectStatusListItemForId(this.objecttypeid, objects, id)
                return { ...base, lines: idToLines[id] ?? [] }
            })
            .sortBy((o: IObjectStatusListItem) => o.objectName.toLowerCase())
            .value()
    }

    protected override updateStep() {
        switch (this.step()) {

            case Step.Start: {
                if (this.pv.isMinSuperManager()) {
                    this.req_getcompanysyncsteps
                        .request()
                        .then((data: ICompanySyncstep[]) => this.syncsteps = data.map(o => o.syncstep))
                        .then(() => this.exportSyncsteps = this.getExportSyncsteps())
                        .then(() => this.step(Step.Init))
                } else {
                    this.req_getsyncsteps
                        .request()
                        .then((data: ISyncstep[]) => this.syncsteps = data)
                        .then(() => this.exportSyncsteps = this.getExportSyncsteps())
                        .then(() => this.step(Step.Init))
                }
                return
            }

            case Step.DownloadIds: {
                this.serviceName = 'fullobjectexport'
                super.updateStep()
                break
            }

            default:
                super.updateStep()
        }
    }

    protected override process() {
        this.submitSuccess = []
        this.submitError = []
        this.successStepResults = []
        this.restoredList = []
        this.errorsReexportInfo = []
        this.errorsAlreadyRestoredInfo = []
        this.errorsFailure = []

        const objectIds = _.cloneDeep(this.data)
        let objects: nsSyncFields.TObjectData_basic[]

        return Promise.resolve()
            .then(async () => {
                this.messageData({message: PLEASE_WAIT_MESSAGE})
                const objectDefinition = this.objectDefinition
                const syncfieldNames: TSyncfield[] = (['Id'] as TSyncfield[])
                    .concat(objectDefinition.syncfieldDisplayName ?? [])
                const syncfieldids: string = await nsSyncFields.getSyncfieldIdsFromNames(this.objecttypeid, ...syncfieldNames)
                const ruleset = nsRuleset.generateRuleset({
                    rules: [nsRuleset.generateRule({
                        attribute: 'Id',
                        argumentoperator: 'Inneholder en av',
                        argument: objectIds.join(','),
                    })]
                })
                const params = getSearchObjectParams({
                    objecttypeid: this.objecttypeid,
                    onlytuples: true,
                    resultsprpage: MAX_RESULTS_PER_PAGE,
                    syncfieldids: syncfieldids,
                    jruleset: JSON.stringify(ruleset),
                })
                return this.req_searchobject
                    .request(params)
                    .then((data: string[][]) => {
                        const type = getObjectType(this.objecttypeid)
                        objects = data.map(o => nsSyncFields.convertTuplesToObjectData(type, syncfieldNames, o))
                    })

            })
            .then(() => {
                const doNextObject = (): Promise<any> => {
                    const numTotal = this.data.length;
                    const numProcessed = numTotal - objectIds.length;
                    this.percentComplete(numProcessed / numTotal);
                    const message = `((restoring|td)) [${numProcessed}/${numTotal}] ${SPINNER}`;
                    this.messageData({message: message})

                    if (!objectIds.length) {
                        return Promise.resolve()
                    }
                    const objectId = objectIds.shift()
                    const syncstepIds = this._selectedExportSteps().map(o => o.syncstep.id)

                    const doNextSyncstep = (): Promise<any> => {
                        if (!syncstepIds.length) {
                            return Promise.resolve()
                        }
                        const syncstepId = syncstepIds.shift()
                        const exportStep = this._selectedExportSteps().find((o) => o.syncstep.id == syncstepId)
                        const p = new PromiseRequest(this.serviceName);
                        this.promiseRequests.push(p);
                        const params = {
                            objecttypeid: this.params.objecttypeid,
                            objectid: objectId,
                            syncstepid: syncstepId,
                            test: false,
                        }
                        return p
                            .request(params)
                            .then(() => {
                                this.submitSuccess.push(objectId)
                                this.successStepResults.push({ id: objectId, stepLabel: exportStep?.name ?? "" })
                                return doNextSyncstep()
                            })
                            .catch((reason: string) => {
                                this.submitError.push({
                                    message: `${exportStep?.name ?? "?"}: ${reason}`,
                                    id: objectId,
                                })
                                return doNextSyncstep()
                            })
                    }

                    const all: Promise<any>[] = []
                    for (let i = 0; i < Math.min(this.numTotal, this.maxConcurrentRequests); i++) {
                        all.push(doNextSyncstep());
                    }
                    return Promise.all(all).then(() => doNextObject())
                }

                return doNextObject()
            })
            .then(() => {
                this.restoredList = this.buildObjectStatusListFromLines(
                    objects,
                    this.successStepResults,
                    (r) => r.id,
                    (r) => r.stepLabel,
                )
                const reexportById: Record<string, string[]> = {}
                const alreadyRestoredById: Record<string, string[]> = {}
                const failureById: Record<string, string[]> = {}
                for (const e of this.submitError ?? []) {
                    const id = String(e.id)
                    if (isObjectAlreadyInReexportQueueError(e.message)) {
                        ;(reexportById[id] ??= []).push(e.message)
                    } else if (isObjectAlreadyRestoredInfoError(e.message)) {
                        ;(alreadyRestoredById[id] ??= []).push(e.message)
                    } else {
                        ;(failureById[id] ??= []).push(e.message)
                    }
                }
                this.errorsReexportInfo = this.buildObjectStatusListFromIdToLines(objects, reexportById)
                this.errorsAlreadyRestoredInfo = this.buildObjectStatusListFromIdToLines(
                    objects,
                    alreadyRestoredById,
                )
                this.errorsFailure = this.buildObjectStatusListFromIdToLines(objects, failureById)
            })
            .then(() => this.onProcessComplete())
            .catch((reason: string) => {
                this.step(Step.Complete);
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    protected getExportSyncsteps(): IExportStep[] {
        return _(this.syncsteps)
            .filter((s: ISyncstep) => s.isexportable && s.id !== SyncstepId.Synchronization)
            .map((s: ISyncstep) => {
                const regExp = /Export\s*(.*)/i;
                const ret: IExportStep = {
                    name: regExp.test(s.name) ? (regExp.exec(s.name)?.[1] ?? s.name) : s.name,
                    syncstep: s,
                };
                return ret;
            })
            .sortBy((e: IExportStep) => e.name.toLowerCase())
            .value()
    }

    protected override onProcessComplete(): Promise<any> {
        return this.req_emptycache
            .request()
            .then(() => {
                global.cacheManager.removeCache('searchobject')
                global.cacheManager.removeCache('getobject')
                super.onProcessComplete()
            })
    }

    private areAllStepsSelected(): boolean {
        if (!this.isStep(Step.Init)) {
            return false
        }
        const a = this._selectedExportSteps().length
        const b = this.exportSyncsteps.length
        return !!a && !!b && a == b
    }

    private areSomeStepsSelected(): boolean {
        const a = this._selectedExportSteps().length
        const b = this.exportSyncsteps.length
        return a > 0 && a < b
    }

    private allSyncstepsChecked = ko.computed({
        read: (): boolean => {
            return this.areAllStepsSelected()
        },
        write: (bool: boolean) => {
            if (bool) {
                this._selectedExportSteps(_.clone(this.exportSyncsteps))
            } else {
                this._selectedExportSteps([])
            }
        }
    });

    override dispose() {
        super.dispose()
        this.req_emptycache.abort()
        this.req_getcompanysyncsteps.abort()
        this.req_getsyncsteps.abort()
        this.allSyncstepsChecked.dispose()
        this.req_searchobject.abort()
    }
}
